/**
 * Contacts — same picker UI as the officer schedule's "Send to WhatsApp" flow
 * (OfficerEditPopup), reusing the same contact data and QR step, just without
 * a pre-filled schedule message: opening the chat is the whole point.
 *
 * The list (see ContactPicker) is split into one tab per team (one sheet per team in
 * the uploaded workbook), and anyone can replace it by uploading a new workbook from here.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiClient } from '@/api/client';
import { useNotification } from '@/contexts/NotificationContext';
import { WaIcon, WhatsAppQrStep } from '@/components/WhatsAppShared';
import { ContactPicker } from '@/components/ContactPicker';
import type { RecipientPill } from '@/components/WhatsAppShared';
import type { ContactList } from '@/types/api';
import { readPickedFile } from '@/utils/fileInput';
import { UI_SCALE, popupMaxH } from '@/utils/uiScale';

function normaliseSgPhone(raw: string): string {
  let digits = raw.replace(/\D/g, '');
  if (digits.length === 8 && /^[689]/.test(digits)) digits = '65' + digits;
  return digits;
}

function formatUpdated(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('en-SG', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function ContactsModal({ onClose }: { onClose: () => void }) {
  const { notifySuccess, notifyError } = useNotification();
  const [data, setData] = useState<ContactList | null>(null);
  const [pendingRecipient, setPendingRecipient] = useState<{ phone: string; label: string; pill?: RecipientPill } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadWarnings, setUploadWarnings] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const lastPickedRef = useRef<File | null>(null);

  const applyList = (list: ContactList) => setData(list);

  useEffect(() => {
    let cancelled = false;
    apiClient.getContacts()
      .then((result) => { if (!cancelled) applyList(result); })
      .catch(() => {
        if (!cancelled) setData({ teams: [], contacts: [], updated_at: null, updated_by: null, filename: null });
      });
    return () => { cancelled = true; };
  }, []);

  // onChange and onInput both fire on most browsers — see readPickedFile for
  // why both are wired; lastPickedRef drops the duplicate.
  async function handleFileChange(e: React.SyntheticEvent<HTMLInputElement>) {
    const input = e.currentTarget;
    const file = await readPickedFile(input);
    if (!file || file === lastPickedRef.current) return;
    lastPickedRef.current = file;
    window.setTimeout(() => { lastPickedRef.current = null; }, 1000);
    if (!/\.xlsx$|\.xlsm$/i.test(file.name)) {
      notifyError('Please select an .xlsx Excel file.');
      input.value = '';
      return;
    }
    setUploading(true);
    setUploadWarnings([]);
    try {
      const result = await apiClient.uploadContacts(file);
      applyList(result);
      setUploadWarnings(result.warnings ?? []);
      notifySuccess(`Contact list updated — ${result.contacts.length} officers across ${result.teams.length} team${result.teams.length === 1 ? '' : 's'}`);
    } catch (err) {
      notifyError(`Could not upload contact list: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  const openQrFor = (rawPhone: string, label?: string, pill?: RecipientPill) => {
    const digits = normaliseSgPhone(rawPhone);
    if (!digits) return;
    setPendingRecipient({ phone: digits, label: label ?? rawPhone, pill });
  };

  const closeContactsFlow = () => {
    onClose();
  };

  // Portalled to document.body: this modal is opened from a button that itself
  // portals into the tab bar, so this component can be reached (and left open)
  // while its actual React parent — HomeTab, wrapped in a display:none div when
  // Settings is the active tab — isn't visible in the layout at all. Without the
  // portal, a display:none ancestor hides this fixed-position overlay too, since
  // "position: fixed" doesn't escape a display:none parent.
  return createPortal(
    <div
      onClick={closeContactsFlow}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 4600,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'white', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
          width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', overflow: 'hidden',
          zoom: UI_SCALE, maxHeight: popupMaxH(80) }}
      >
        {/* QR step — no message here, so WhatsAppQrStep skips the preview
            entirely rather than showing an empty box. */}
        {pendingRecipient ? (
          <WhatsAppQrStep
            phone={pendingRecipient.phone}
            recipientLabel={pendingRecipient.label}
            recipientPill={pendingRecipient.pill}
            onBack={() => setPendingRecipient(null)}
            onClose={closeContactsFlow}
          />
        ) : (
        <>
        {/* Header */}
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <WaIcon size={20} />
            <span style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>Contacts</span>
          </div>
          <button
            onClick={closeContactsFlow}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#888', padding: 0 }}
          >
            ✕
          </button>
        </div>

        <ContactPicker
          data={data}
          emptyHint="No contacts yet — upload the contact list below"
          onPick={(r) => openQrFor(r.phone, r.label, r.pill)}
        />

        {/* Footer — the QR step has its own "← Back" instead */}
        <div style={{ padding: '10px 18px', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
          {uploadWarnings.length > 0 && (
            <div style={{ fontSize: 11, color: '#92400e', background: '#fef3c7', borderRadius: 6,
              padding: '6px 8px', marginBottom: 8 }}>
              {uploadWarnings.map((w, i) => <div key={i}>{w}</div>)}
            </div>
          )}
          <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8 }}>
            {data?.updated_at
              ? <>Last updated {formatUpdated(data.updated_at)}{data.updated_by ? ` by ${data.updated_by}` : ''}</>
              : data ? 'Contact list not uploaded yet' : '\u00a0'}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
            <button
              onClick={closeContactsFlow}
              style={{ padding: '7px 16px', background: '#f8f9fa', border: '1px solid #ddd',
                borderRadius: 7, fontSize: 13, cursor: 'pointer' }}
            >
              Back
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              title="Replace the contact list with an Excel file (one sheet per team; NAME, SECTIONS and H/PHONE columns)"
              style={{ padding: '7px 14px', background: '#0f7a3d', color: 'white', border: 'none',
                borderRadius: 7, fontSize: 13, fontWeight: 600,
                cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.6 : 1 }}
            >
              {uploading ? 'Uploading…' : 'Upload contact list'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xlsm"
              style={{ display: 'none' }}
              onChange={handleFileChange}
              onInput={handleFileChange}
            />
          </div>
        </div>
        </>
        )}
      </div>
    </div>,
    document.body
  );
}
